import type { State } from '@contracts/order/valueNormalizers/State';
import { abbreviationFromState } from '@contracts/order/valueNormalizers/State';
import { FormDropdown } from '@src/common/components/FormDropdown';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import { prefixFieldNameIfPrefixExists } from '@src/common/utils/formUtils';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { ControllerFormTextInput } from '@src/orders/PermitFormSections/ControllerFormTextInput';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import {
  TrailerLicensePlateField,
  TrailerLicensePlateStateField,
  TrailerLicensePlateTypeField,
} from '@src/permitField/TrailerInformationFields';
import type { TrailerFormSchema } from '@src/trailer/TrailerFormSchema';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

type Props = {
  control: Control<yup.InferType<typeof TrailerFormSchema>>;
  readonly?: boolean;
  fieldsPrefix?: string;
  fields: string[];
};

export const TrailerLicensePlateForm = ({
  control,
  readonly,
  fieldsPrefix,
  fields,
}: Props) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();

  if (fields.length <= 0) {
    return null;
  }

  const getFieldName = (formFieldName: string, fieldPrefix?: string) =>
    prefixFieldNameIfPrefixExists<yup.InferType<typeof TrailerFormSchema>>(
      formFieldName,
      fieldPrefix,
    );

  return (
    <Stack direction="row" gap={theme.spacing['3']}>
      <ConditionalField
        fields={fields}
        conditions={TrailerLicensePlateField.fieldName}
      >
        <ControllerFormTextInput
          disabled={
            readonly || isFieldDisabled(TrailerLicensePlateField.fieldName)
          }
          control={control}
          label={t(`fields.${TrailerLicensePlateField.fieldName}`)}
          fieldName={getFieldName(
            TrailerLicensePlateField.fieldName,
            fieldsPrefix,
          )}
        />
      </ConditionalField>
      <ConditionalField
        fields={fields}
        conditions={TrailerLicensePlateStateField.fieldName}
      >
        <FormDropdown
          disabled={
            readonly || isFieldDisabled(TrailerLicensePlateStateField.fieldName)
          }
          fieldName={getFieldName(
            TrailerLicensePlateStateField.fieldName,
            fieldsPrefix,
          )}
          options={TrailerLicensePlateStateField.options.map(
            (option: State) => ({
              value: option,
              label: abbreviationFromState(option),
            }),
          )}
          control={control}
          label={t(`fields.${TrailerLicensePlateStateField.fieldName}`)}
        />
      </ConditionalField>
      <ConditionalField
        fields={fields}
        conditions={TrailerLicensePlateTypeField.fieldName}
      >
        <FormDropdown
          disabled={
            readonly || isFieldDisabled(TrailerLicensePlateTypeField.fieldName)
          }
          fieldName={getFieldName(
            TrailerLicensePlateTypeField.fieldName,
            fieldsPrefix,
          )}
          options={TrailerLicensePlateTypeField.options.map((option) => ({
            value: option,
            label: option,
          }))}
          control={control}
          label={t(`fields.${TrailerLicensePlateTypeField.fieldName}`)}
        />
      </ConditionalField>
    </Stack>
  );
};
